import { Typography } from '@mui/material';
import type { Dispatch, ReactElement, SetStateAction } from 'react';
import React from 'react';
import type { Attachment, AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import type { Reference } from 'sr-types/lib/search/v1/graphql';
import type { EntityName } from '../auth/Security';
import { EntitySecurityProvider } from '../auth/Security';
import SidePanel from '../components/layout/SidePanel';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { V } from '../Layout';
import { ShareSettingsProvider } from '../share';
import { client as attachmentClient } from './attachmentsApi';
import AttachmentsDisplay from './AttachmentsDisplay';
import type { AttachmentLinkDialogTriggerProps } from './types';

type AttachmentsProps = AttachmentLinkDialogTriggerProps & {
    id: string;
    /** Overrides the built-in save; used by surfaces that keep attachments in form state until the entity is saved. */
    onSave?: (entityId: string, input: AttachmentInput[], contextTerm: string) => Promise<any>;
    entityReference?: Reference;
    onClose?: () => void;
    showTitle?: boolean;
    title?: string | ReactElement;
    maxFileSize?: number;
    acceptedFiles?: Array<string>;
    filesLimit?: number;
    attachmentsInState?: Array<AttachmentInput>;
    onDeleteAttachmentFromState?: (attachments: AttachmentInput[], input: AttachmentInput) => AttachmentInput[];
    disableDropzone?: boolean;
    hideDropzone?: boolean;
    hideAction?: boolean;
    isInSidePanel?: boolean;
    setIsInUploadMode?: Dispatch<SetStateAction<boolean>>;
    contextTerm?: string;
    entity: EntityName;
    entityOriginId?: string;
    defaultMandatoryTags?: Array<string>;
    enableLinkAttachment?: boolean;
    leftAlignContent?: boolean;
    disableButtons?: boolean;
    autoOpenFilePickerTrigger?: number;
    /** Called after an auto-open file picker request is consumed. */
    onAutoOpenFilePickerHandled?: () => void;
    enableSelection?: boolean;
    disabledSelectedAttachmentIds?: ReadonlySet<string>;
    onAddSelectedAttachments?: (attachments: Attachment[]) => void;
    onCancelSelection?: () => void;
    /** Called after saveAttachments returns ids for newly saved files or links. */
    onSaveAttachments?: (attachments: Attachment[]) => void;
};

export default function Attachments(props: AttachmentsProps) {
    const {
        title = <I18n token="attachments.title" />,
        showTitle = true,
        onClose,
        hideAction = false,
        isInSidePanel = true
    } = props;

    const securedActions = [
        {
            icon: Icons.Close,
            label: <I18n token="dialog.close" />,
            onClick: onClose
        }
    ];

    return (
        <ShareSettingsProvider throwOnError client={attachmentClient}>
            <EntitySecurityProvider entityName={ENTITY_CONSTANTS.attachment} entitySecurity={{}}>
                {isInSidePanel ? (
                    <SidePanel
                        id={'attachments-container'}
                        dataTestId={'shareSettingsPanel'}
                        title={title}
                        securedActions={hideAction ? [] : securedActions}
                    >
                        <AttachmentsDisplay {...props} />
                    </SidePanel>
                ) : (
                    <V gap={1}>
                        {showTitle && <Typography fontWeight="bold">{title}</Typography>}
                        <AttachmentsDisplay {...props} />
                    </V>
                )}
            </EntitySecurityProvider>
        </ShareSettingsProvider>
    );
}
